import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  ViewChild,
  forwardRef,
  OnChanges,
  SimpleChanges,
  EventEmitter,
  Output,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import { DropdownAlign, DropdownDirection } from '@ispui/dropdown';
import { ISPUIDropdown } from '@ispui/dropdown/dist/types/components/dropdown/dropdown';
import {
  Option,
  OptionUI,
  SelectBuilderParams,
  SelectEvent,
} from '@ispui/select/dist/types';

import { matchMediaBreakpointDown } from 'common/media';
import { Z_INDEX } from 'common/z-index';

import { ISelectComponent } from './model/select.component.interface';

@Component({
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectComponent),
      multi: true,
    },
  ],
  selector: 'isp-select',
  styleUrls: ['./scss/select.scss'],
  templateUrl: './select.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SelectComponent
  implements ControlValueAccessor, OnChanges, ISelectComponent
{
  private isDropdownOpen: boolean;

  private get isHasConfirm(): boolean {
    return Boolean(this.confirmMsg || this.resetMsg);
  }

  selectBuilderParams: SelectBuilderParams;

  value: string[] | string;

  internalOptions: Option[] = [];

  @Input() disabled: boolean;

  @Input() id: string;

  @Input() multiple: boolean;

  @Input() label?: string;

  @Input() required: boolean;

  @Input() options: Option[];

  @Input() placeholder: string;

  @Input() anchorWidth: string;

  @Input() anchorType: SelectBuilderParams['button'];

  @Input() anchorTextFuncHTML?: SelectBuilderParams['buttonTextFuncHTML'];

  @Input() anchorTextFunc?: SelectBuilderParams['buttonTextFunc'];

  @Input() listWidth: string;

  @Input() notFoundText: string;

  @Input() selectAllText: string;

  @Input() dropdownPopupClass: string;

  @Input() dropdownParentSelector?: string;

  @Input() dropdownScrollSelector?: string;

  @Input() dropdownDirection?: DropdownDirection;

  @Input() alignment?: DropdownAlign;

  @Input() anchorMaxWidth?: string = '500px';

  @Input() confirmMsg: string;

  @Input() resetMsg: string;

  @Input() displayClear: boolean;

  @Output() readonly optionClick = new EventEmitter<OptionUI>();

  @ViewChild('select', {
    read: ElementRef,
  })
  select: ElementRef<HTMLIspuiSelectElement>;

  constructor(private readonly cdr: ChangeDetectorRef) {}

  private onChange: (value: string[] | string) => void = () => undefined;

  private onTouch: () => void = () => undefined;

  private getSelectBuilderParams(): SelectBuilderParams {
    const dropdownMaxWidth =
      this.anchorType === 'chip' ? '300px' : this.listWidth;
    const dropdownMinWidth =
      this.anchorType === 'chip' ? '200px' : this.listWidth;

    const isLessSm = matchMediaBreakpointDown('sm');
    const selectBuilderParams: SelectBuilderParams = {
      legend: this.label,
      id: this.id,
      placeholder: this.placeholder,
      width: isLessSm ? '100%' : this.anchorWidth,
      maxWidth: isLessSm ? '100%' : this.anchorMaxWidth,
      notFoundText: this.notFoundText,
      searchCount: 10,
      toggle: this.options.length > 0,
      toggleOnText: this.selectAllText,
      toggleOffText: this.selectAllText,
      apply: Boolean(this.confirmMsg),
      applyText: this.confirmMsg,
      reset: Boolean(this.resetMsg),
      resetText: this.resetMsg,
      reflectColor: true,
      button: this.anchorType || 'legend',
      $dropdown: {
        popupClass: this.dropdownPopupClass,
        parentSelector: this.dropdownParentSelector,
        scrollSelector: this.dropdownScrollSelector,
        direction: this.dropdownDirection,
        strictDirection: Boolean(this.dropdownDirection),
        alignment: this.alignment,
        borderOffset: 0,
        // @TODO i.ablov add zIndex to select dropdown builder params
        zIndex: Z_INDEX.zSelect,
      } as any,
      $button: {
        style: {
          /* eslint-disable @typescript-eslint/naming-convention */
          '--ispui-select-button-legend__width':
            'var(--ispui-input__width, 400px)',
          '--ispui-select-button__width': 'var(--ispui-input__width, 400px)',
          '--ispui-select-button-legend__pl': '15px',
          '--ispui-select-button__pl': '15px',
          /* eslint-enable @typescript-eslint/naming-convention */
        },
      },
      $list: {
        style: {
          marginRight: '0px',
          paddingRight: '10px',
          // @WARN '-10px' because paddingRight above
          'min-width': `calc(${dropdownMinWidth} - 10px)`,
          'max-width': `calc(${dropdownMaxWidth} - 10px)`,
          /* eslint-disable @typescript-eslint/naming-convention */
          '--ispui-select-list__max-height': '280px',
          /* eslint-enable @typescript-eslint/naming-convention */
        },
      },
      $search: {
        style: {
          padding: '10px 10px',
          margin: '0',
        },
      },
      $toggle: {
        style: {
          /* eslint-disable @typescript-eslint/naming-convention */
          '--ispui-select-toggle__checkbox-pl': '15px',
          /* eslint-enable @typescript-eslint/naming-convention */
          padding: '5px 0px 15px 20px',
        },
      },
      $apply: this.isHasConfirm
        ? {
            style: {
              marginTop: '20px',
              marginBottom: '10px',
            },
          }
        : undefined,
    };

    if (this.anchorTextFuncHTML) {
      selectBuilderParams.buttonTextFuncHTML = this.anchorTextFuncHTML;
    }

    if (this.anchorTextFunc) {
      selectBuilderParams.buttonTextFunc = this.anchorTextFunc;
    }

    if (this.displayClear) {
      selectBuilderParams.$button!.displayClear = true;
      selectBuilderParams.$button!.style!['--ispui-select-button-chip__pr'] =
        '25px';
    }

    return selectBuilderParams;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      this.options &&
      this.selectBuilderParams &&
      changes.options &&
      Object.keys(changes).length > 1
    ) {
      this.selectBuilderParams = this.getSelectBuilderParams();
      // @HACK select can't update options and selectBuilderParams simultaneously
      // so add tick between updates
      setTimeout(() => {
        this.internalOptions = this.options;
        this.cdr.markForCheck();
      });
      return;
    }

    this.internalOptions = this.options;
    if (
      changes.options &&
      Object.keys(changes).length === 1 &&
      this.selectBuilderParams
    ) {
      return;
    }
    this.selectBuilderParams = this.getSelectBuilderParams();
  }

  onModelChange(value: string[] | string): void {
    if (value === this.value) {
      return;
    }

    this.value = value;

    this.onChange(value);
    this.onTouch();
  }

  writeValue(value: string[] | string | null): void {
    if (this.multiple) {
      const arrayValue = Array.isArray(value) ? value : [value];
      this.value = arrayValue.filter((v): v is string => Boolean(v));
    } else {
      this.value = value ? value : '';
    }

    this.cdr.markForCheck();
  }

  registerOnChange(onChange: (value: string[] | string) => void): void {
    this.onChange = onChange;
  }

  registerOnTouched(onTouch: () => void): void {
    this.onTouch = onTouch;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  keyupEnter(event: KeyboardEvent): void {
    if (this.isDropdownOpen) {
      event.stopPropagation();
    }
  }

  checkDropdownOpen(event: CustomEvent<SelectEvent>): void {
    const eventsWithOpenDropdown = ['dropdown-open', 'search', 'option-click'];
    this.isDropdownOpen = eventsWithOpenDropdown.includes(event.detail.name);

    if (event.detail.name === 'dropdown-closing-start') {
      this.onTouch();
    }

    if (event.detail.name === 'dropdown-close' && this.isHasConfirm) {
      this.resetValue();
    }

    if (event.detail.name === 'option-click' && event.detail.data) {
      this.optionClick.emit(event.detail.data);
    }
  }

  resetValue(): void {
    (this.select.nativeElement as any).builderInstance.builder.handleReset();
  }

  closeDropdown(): void {
    const dropdown = (this.select.nativeElement as any).builderInstance.builder
      .dropdown as ISPUIDropdown;

    if (dropdown.isVisible) {
      dropdown.toggleDropdown();
    }
  }
}
